const SIZE_GUIDE_ELEMENT = 'size-guide-block';

class SizeGuideBlock extends HTMLElement {
  #fallback;
  #sourceTrigger;
  #productScope;
  #observer;
  #dialogObserver;
  #placementFrame;
  #modalPromotionFrame;
  #blockId;
  #dialog;
  #productFormButtons;
  #shopifyBlockWrapper;

  connectedCallback() {
    if (this.dataset.sizeGuideReady === 'true') return;

    this.dataset.sizeGuideReady = 'true';
    this.#blockId = this.id.replace(/^SizeGuide-/, '');
    this.#fallback = this.querySelector('.size-guide-block__fallback');
    this.#sourceTrigger = this.#fallback?.querySelector('.size-guide-block__trigger');
    this.#productScope = this.closest('.product-details');
    this.#dialog = this.querySelector('dialog.size-guide-block__dialog');

    this.addEventListener('click', this.#handleClick);
    window.addEventListener('resize', this.#handleResize);
    this.#setUnit(this.dataset.defaultUnit === 'cm' ? 'cm' : 'inch');

    if (this.#dialog) {
      this.#dialogObserver = new MutationObserver(this.#scheduleModalPromotion);
      this.#dialogObserver.observe(this.#dialog, {
        attributes: true,
        attributeFilter: ['open'],
      });
    }

    if (!this.#productScope || !this.#sourceTrigger) return;

    this.#observer = new MutationObserver(this.#schedulePlacement);
    this.#observer.observe(this.#productScope, {
      childList: true,
      subtree: true,
    });

    this.#schedulePlacement();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    window.removeEventListener('resize', this.#handleResize);
    this.#observer?.disconnect();
    this.#dialogObserver?.disconnect();

    if (this.#placementFrame) {
      cancelAnimationFrame(this.#placementFrame);
    }

    if (this.#modalPromotionFrame) {
      cancelAnimationFrame(this.#modalPromotionFrame);
    }

    this.#removeInlineTriggers();
    this.#removeLabelMarkers();
    this.#restoreProductSpacing();
    delete this.dataset.sizeGuideReady;
  }

  #handleClick = (event) => {
    const button = event.target.closest('[data-size-guide-unit]');
    if (!button || !this.contains(button)) return;

    const unit = button.dataset.sizeGuideUnit;
    if (unit === 'inch' || unit === 'cm') {
      this.#setUnit(unit);
    }
  };

  #setUnit(unit) {
    this.dataset.unit = unit;

    for (const button of this.querySelectorAll('[data-size-guide-unit]')) {
      button.setAttribute('aria-pressed', String(button.dataset.sizeGuideUnit === unit));
    }

    for (const valueElement of this.querySelectorAll('[data-size-guide-value]')) {
      const inchValue = (valueElement.dataset.inch || '').trim();

      if (unit === 'inch' || inchValue === '') {
        valueElement.textContent = inchValue;
        continue;
      }

      if (!/^-?\d+(?:\.\d+)?$/.test(inchValue)) {
        valueElement.textContent = inchValue;
        continue;
      }

      const centimetres = Number(inchValue) * 2.54;
      valueElement.textContent = centimetres.toFixed(1).replace(/\.0$/, '');
    }
  }

  #schedulePlacement = () => {
    if (this.#placementFrame) return;

    this.#placementFrame = requestAnimationFrame(() => {
      this.#placementFrame = 0;
      this.#placeTrigger();
    });
  };

  #scheduleModalPromotion = () => {
    if (this.#modalPromotionFrame) return;

    this.#modalPromotionFrame = requestAnimationFrame(() => {
      this.#modalPromotionFrame = 0;

      if (!this.#dialog?.open || this.#dialog.matches(':modal')) return;

      this.#dialog.close();
      this.#dialog.showModal();
    });
  };

  #handleResize = () => {
    this.#scheduleModalPromotion();
    this.#schedulePlacement();
  };

  #placeTrigger() {
    const optionName = (this.dataset.optionName || 'Size').trim().toLocaleLowerCase();
    const optionNodes = this.#productScope.querySelectorAll('[data-option-name]');
    const optionNode = Array.from(optionNodes).find(
      (node) => (node.dataset.optionName || '').trim().toLocaleLowerCase() === optionName
    );

    const optionGroup = optionNode?.closest('.variant-option');
    const targetLabel = optionGroup?.matches('fieldset')
      ? optionGroup.querySelector(':scope > legend')
      : optionGroup?.querySelector(':scope > label');

    if (!targetLabel) {
      this.#removeInlineTriggers();
      this.#removeLabelMarkers();
      this.#restoreProductSpacing();
      this.#fallback.hidden = false;
      return;
    }

    const labelRow = this.#getOrCreateLabelRow(targetLabel);

    this.#removeInlineTriggers(labelRow);
    this.#removeLabelMarkers(targetLabel);

    let inlineTrigger = Array.from(labelRow.querySelectorAll('[data-size-guide-inline-trigger]')).find(
      (trigger) => trigger.dataset.sizeGuideInlineTrigger === this.#blockId
    );

    if (!inlineTrigger) {
      inlineTrigger = this.#sourceTrigger.cloneNode(true);
      inlineTrigger.dataset.sizeGuideInlineTrigger = this.#blockId;
      labelRow.append(inlineTrigger);
    }

    targetLabel.classList.add('size-guide-block__variant-label');
    targetLabel.dataset.sizeGuideLabelOwner = this.#blockId;
    this.#alignTrigger(targetLabel, labelRow, inlineTrigger);
    this.#compactProductSpacing();
    this.#fallback.hidden = true;
  }

  #alignTrigger(targetLabel, labelRow, inlineTrigger) {
    const labelText = labelRow.querySelector(':scope > .size-guide-block__label-text');
    const generatedLabel = getComputedStyle(targetLabel, '::before').content;
    const hasGeneratedLabel = !['none', 'normal', '""', "''"].includes(generatedLabel);
    const triggerWidth = Math.ceil(inlineTrigger.getBoundingClientRect().width);

    targetLabel.style.position = 'relative';
    targetLabel.style.boxSizing = 'border-box';
    targetLabel.style.paddingInlineEnd = `${triggerWidth + 16}px`;
    targetLabel.style.marginBlockEnd = '16px';
    targetLabel.style.setProperty('margin-bottom', '16px', 'important');

    labelRow.style.display = 'contents';

    if (labelText) {
      labelText.style.display = hasGeneratedLabel ? 'none' : 'inline';
    }

    inlineTrigger.style.position = 'absolute';
    inlineTrigger.style.insetBlockStart = '50%';
    inlineTrigger.style.insetInlineEnd = '0';
    inlineTrigger.style.minHeight = '0';
    inlineTrigger.style.margin = '0';
    inlineTrigger.style.transform = 'translateY(-50%)';
    inlineTrigger.style.whiteSpace = 'nowrap';
  }

  #compactProductSpacing() {
    this.dataset.inlinePlacement = 'true';
    this.style.position = 'absolute';
    this.style.width = '0';
    this.style.height = '0';
    this.style.margin = '0';
    this.style.padding = '0';

    this.#shopifyBlockWrapper = this.closest('.shopify-block');

    if (this.#shopifyBlockWrapper) {
      this.#shopifyBlockWrapper.style.setProperty('position', 'absolute', 'important');
      this.#shopifyBlockWrapper.style.setProperty('width', '0', 'important');
      this.#shopifyBlockWrapper.style.setProperty('height', '0', 'important');
      this.#shopifyBlockWrapper.style.setProperty('margin', '0', 'important');
      this.#shopifyBlockWrapper.style.setProperty('padding', '0', 'important');
    }

    this.#productFormButtons = this.#productScope.querySelector('.product-form-buttons');

    if (this.#productFormButtons) {
      this.#productFormButtons.style.marginBlockStart = '8px';
      this.#productFormButtons.style.setProperty('margin-top', '8px', 'important');
    }
  }

  #restoreProductSpacing() {
    delete this.dataset.inlinePlacement;
    this.style.removeProperty('position');
    this.style.removeProperty('width');
    this.style.removeProperty('height');
    this.style.removeProperty('margin');
    this.style.removeProperty('padding');

    if (this.#shopifyBlockWrapper) {
      this.#shopifyBlockWrapper.style.removeProperty('position');
      this.#shopifyBlockWrapper.style.removeProperty('width');
      this.#shopifyBlockWrapper.style.removeProperty('height');
      this.#shopifyBlockWrapper.style.removeProperty('margin');
      this.#shopifyBlockWrapper.style.removeProperty('padding');
      this.#shopifyBlockWrapper = null;
    }

    if (this.#productFormButtons) {
      this.#productFormButtons.style.removeProperty('margin-block-start');
      this.#productFormButtons.style.removeProperty('margin-top');
      this.#productFormButtons = null;
    }
  }

  #getOrCreateLabelRow(targetLabel) {
    let labelRow = Array.from(targetLabel.children).find(
      (child) => child.dataset.sizeGuideLabelRow === this.#blockId
    );

    if (labelRow) return labelRow;

    for (const trigger of targetLabel.querySelectorAll('[data-size-guide-inline-trigger]')) {
      if (trigger.dataset.sizeGuideInlineTrigger === this.#blockId) {
        trigger.remove();
      }
    }

    labelRow = document.createElement('span');
    labelRow.className = 'size-guide-block__label-row';
    labelRow.dataset.sizeGuideLabelRow = this.#blockId;

    const labelText = document.createElement('span');
    labelText.className = 'size-guide-block__label-text';

    while (targetLabel.firstChild) {
      labelText.append(targetLabel.firstChild);
    }

    labelRow.append(labelText);
    targetLabel.append(labelRow);

    return labelRow;
  }

  #removeInlineTriggers(exceptWithin = null) {
    if (!this.#productScope) return;

    for (const trigger of this.#productScope.querySelectorAll('[data-size-guide-inline-trigger]')) {
      if (trigger.dataset.sizeGuideInlineTrigger !== this.#blockId) continue;
      if (exceptWithin?.contains(trigger)) continue;
      trigger.remove();
    }
  }

  #removeLabelMarkers(exceptLabel = null) {
    if (!this.#productScope) return;

    for (const label of this.#productScope.querySelectorAll('[data-size-guide-label-owner]')) {
      if (label.dataset.sizeGuideLabelOwner !== this.#blockId) continue;
      if (label === exceptLabel) continue;

      for (const trigger of label.querySelectorAll('[data-size-guide-inline-trigger]')) {
        if (trigger.dataset.sizeGuideInlineTrigger === this.#blockId) {
          trigger.remove();
        }
      }

      const labelRow = Array.from(label.children).find(
        (child) => child.dataset.sizeGuideLabelRow === this.#blockId
      );
      const labelText = labelRow?.querySelector(':scope > .size-guide-block__label-text');

      if (labelRow && labelText) {
        while (labelText.firstChild) {
          label.insertBefore(labelText.firstChild, labelRow);
        }
        labelRow.remove();
      }

      label.classList.remove('size-guide-block__variant-label');
      delete label.dataset.sizeGuideLabelOwner;
      label.style.removeProperty('position');
      label.style.removeProperty('box-sizing');
      label.style.removeProperty('padding-inline-end');
      label.style.removeProperty('margin-block-end');
      label.style.removeProperty('margin-bottom');
    }
  }
}

if (!customElements.get(SIZE_GUIDE_ELEMENT)) {
  customElements.define(SIZE_GUIDE_ELEMENT, SizeGuideBlock);
}